import React, { useMemo, useState, useCallback, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions, ApexAxisChartSeries } from "apexcharts";
import { fixMarkerOverlaps } from "./graphUtils";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CountGame {
  game_date: string;
  g_match_id: string | null;
  opponent: string | null;
  stat_value: number;
  condition_met: boolean;
}

export interface CountPlayer {
  player_name: string;
  team_name: string;
  condition_met_count: number;
  total_games: number;
  condition_pct: number;
  dense_rank: number;
  games: CountGame[];
}

export interface CountGraphData {
  query_type: string;
  chart_type: string;
  stat_label: string;
  threshold_label: string | null;
  labels: string[];
  values: number[];
  players: CountPlayer[];
  total_records: number;
  sql_query?: string;
}

interface CountGraphProps {
  data: CountGraphData;
  chartReady?: boolean;
  statLabel?: string;
}

// ── Palette ───────────────────────────────────────────────────────────────────

const PALETTE = [
  "#3B82F6", "#10B981", "#F59E0B", "#EC4899",
  "#8B5CF6", "#06B6D4", "#F97316", "#14B8A6", "#EF4444", "#84CC16",
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseThresholdValue(label: string | null): number | null {
  if (!label) return null;
  const match = label.match(/[\d.]+/);
  return match ? parseFloat(match[0]) : null;
}

function formatDateShort(dateStr: string): string {
  if (/^\d{4}$/.test(dateStr.trim())) return dateStr;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleDateString("en-US", { month: "short" });
  const year = String(d.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
}

function sortedGames(player: CountPlayer): CountGame[] {
  return [...player.games].sort(
    (a, b) => new Date(a.game_date).getTime() - new Date(b.game_date).getTime()
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&family=JetBrains+Mono:wght@400;500;600&display=swap');

  .ctg * { box-sizing: border-box; }

  .ctg {
    --bg: #ffffff;
    --surface: #f8fafc;
    --border: #e8edf3;
    --border-accent: #cbd5e1;
    --text-primary: #0f172a;
    --text-secondary: #475569;
    --text-muted: #94a3b8;
    font-family: 'Plus Jakarta Sans', sans-serif;
    background: var(--bg);
    border-radius: 24px;
    padding: 24px 28px;
    position: relative;
    width: 100%;
    border: 1px solid var(--border);
    box-shadow:
      0 1px 2px rgba(0,0,0,0.04),
      0 4px 16px rgba(0,0,0,0.06),
      0 16px 48px rgba(0,0,0,0.04);
  }

  .ctg::before {
    content: '';
    position: absolute;
    top: 0; right: 0;
    width: 260px; height: 160px;
    background: radial-gradient(ellipse at top right, rgba(16,185,129,0.05) 0%, transparent 65%);
    pointer-events: none;
    border-radius: 0 24px 0 0;
  }

  .ctg::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, rgba(148,163,184,0.18) 1px, transparent 1px);
    background-size: 28px 28px;
    pointer-events: none;
    border-radius: 24px;
    opacity: 0.55;
    z-index: 0;
  }

  .ctg-chips-wrapper { position: relative; z-index: 1; margin-bottom: 18px; }

  .ctg-chips {
    display: flex; gap: 6px; flex-wrap: wrap;
    padding: 12px 14px;
    background: var(--surface);
    border-radius: 14px;
    border: 1px solid var(--border); max-height: 164px; overflow-y: auto;
  }

  .ctg-chip-all {
    display: flex; align-items: center; gap: 5px;
    padding: 6px 13px; border-radius: 9px;
    font-size: 11px; font-weight: 700;
    border: 1.5px solid var(--border-accent);
    background: white; color: var(--text-secondary);
    cursor: pointer; transition: all 0.18s ease; white-space: nowrap;
  }
  .ctg-chip-all:hover { background: #f0f9ff; border-color: #93c5fd; color: #2563eb; }
  .ctg-chip-all.active { background: #eff6ff; border-color: #93c5fd; color: #2563eb; }

  .ctg-chip {
    display: flex; align-items: center; gap: 7px;
    padding: 5px 10px 5px 8px; border-radius: 9px;
    font-size: 11px; font-weight: 600;
    border: 1.5px solid transparent;
    background: white; color: var(--text-secondary);
    cursor: pointer; transition: all 0.18s cubic-bezier(0.4,0,0.2,1);
    white-space: nowrap; height: 34px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }
  .ctg-chip:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.08); color: var(--text-primary); }
  .ctg-chip.active {
    border-color: var(--chip-border);
    background: var(--chip-bg);
    color: var(--chip-color);
    box-shadow: 0 2px 8px var(--chip-shadow);
  }

  .ctg-swatch {
    width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .ctg-chip.active .ctg-swatch { transform: scale(1.4); box-shadow: 0 0 6px var(--chip-color); }
  .ctg-chip-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 150px; }
  .ctg-chip-stat-badge {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px; font-weight: 600;
    padding: 2px 7px; border-radius: 6px;
    background: rgba(0,0,0,0.06); color: var(--text-secondary);
    flex-shrink: 0; transition: background 0.18s ease, color 0.18s ease;
  }
  .ctg-chip.active .ctg-chip-stat-badge { background: var(--chip-color); color: #ffffff; }
  .ctg-chip-check { font-size: 10px; font-weight: 700; flex-shrink: 0; }

  .ctg-chart-wrapper {
    position: relative; z-index: 2;
    width: 100%; border-radius: 14px;
    overflow-x: auto; overflow-y: hidden;
    background: white; padding-top: 26px; border: 1px solid var(--border);
  }
  .ctg-chart-wrapper .apexcharts-tooltip {
    z-index: 9999 !important; overflow: visible !important;
  }

  .ctg-empty {
    height: 100%; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    color: var(--text-muted);
    font-size: 13px; font-weight: 600; gap: 8px;
  }
  .ctg-empty-icon { font-size: 28px; opacity: 0.35; }

  .ctg-legend-row {
    display: flex; align-items: center; gap: 10px;
    padding: 3px 0; font-size: 11px; color: var(--text-secondary);
  }
  .ctg-legend-dot {
    width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0;
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

const CountGraph: React.FC<CountGraphProps> = ({
  data,
  chartReady = true,
  statLabel = "Stat Value",
}) => {
  const { players, labels, threshold_label } = data;

  const [selectedPlayers, setSelectedPlayers] = useState<Set<number>>(
    () => new Set(Array.from({ length: Math.min(2, players.length) }, (_, i) => i))
  );

  const togglePlayer = useCallback((idx: number) => {
    setSelectedPlayers((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) { next.delete(idx); } else if (next.size >= 2) { return prev; } else { next.add(idx); }
      return next;
    });
  }, []);

  const selectAllPlayers = useCallback(() => {
    if (selectedPlayers.size >= Math.min(2, players.length)) {
      setSelectedPlayers(new Set());
    } else {
      setSelectedPlayers(new Set(Array.from({ length: Math.min(2, players.length) }, (_, i) => i)));
    }
  }, [players.length, selectedPlayers.size]);

  const selectedIndicesOrdered = useMemo(
    () =>
      Array.from(selectedPlayers)
        .filter((idx) => idx >= 0 && idx < players.length)
        .sort((a, b) => a - b),
    [selectedPlayers, players.length]
  );

  const selectedCount = selectedIndicesOrdered.length;  // Assign unique colors to selected players (dedupe if same slot)
  const playerColorMap = useMemo(() => {
    const map = new Map<number, string>();
    const used = new Set<number>();
    selectedIndicesOrdered.forEach((playerIdx) => {
      let slot = playerIdx % PALETTE.length;
      while (used.has(slot)) slot = (slot + 1) % PALETTE.length;
      used.add(slot);
      map.set(playerIdx, PALETTE[slot]);
    });
    return map;
  }, [selectedIndicesOrdered]);



  const gameLookup = useMemo<CountGame[][]>(() => {
    return selectedIndicesOrdered.map((idx) => sortedGames(players[idx]));
  }, [players, selectedIndicesOrdered]);

  const maxGames = useMemo(
    () => Math.max(0, ...gameLookup.map((g) => g.length)),
    [gameLookup]
  );

  // ── x-axis: dates (single) or G1..Gn (multi) ───────────────────────────────
  const xCategories = useMemo(() => {
    if (selectedCount === 1) {
      const g = gameLookup[0];
      if (!g) return [];
      return g.map((game) => formatDateShort(game.game_date));
    }
    return Array.from({ length: maxGames }, (_, i) => `G${i + 1}`);
  }, [selectedCount, gameLookup, maxGames]);

  // ── Series: y = stat_value ─────────────────────────────────────────────────
  const series: ApexAxisChartSeries = useMemo(() => {
    return selectedIndicesOrdered
      .map((playerIdx, seriesIdx) => {
        const player = players[playerIdx];
        if (!player) return null;
        const games = gameLookup[seriesIdx];
        if (!games) return null;

        if (selectedCount === 1) {
          return {
            name: labels[playerIdx] ?? player.player_name,
            data: games.map((g) => g.stat_value),
          };
        }
        return {
          name: labels[playerIdx] ?? player.player_name,
          data: games.map((g, i) => ({
            x: `G${i + 1}`,
            y: g.stat_value,
          })),
        };
      })
      .filter(Boolean) as ApexAxisChartSeries;
  }, [players, selectedIndicesOrdered, selectedCount, gameLookup, labels]);

  // ── Threshold line annotation ──────────────────────────────────────────────
  const thresholdValue = useMemo(
    () => parseThresholdValue(threshold_label),
    [threshold_label]
  );

  const thresholdAnnotation = useMemo(() => {
    if (thresholdValue === null) return undefined;
    return {
      yaxis: [
        {
          y: thresholdValue,
          borderColor: "rgba(16,185,129,0.5)",
          borderWidth: 2,
          strokeDashArray: 6,
          label: {
            text: threshold_label ?? "",
            position: "right" as const,
            style: {
              color: "#065f46",
              background: "#ecfdf5",
              fontSize: "11px",
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: "600",
              borderRadius: "4px",
              borderColor: "#6ee7b7",
              borderWidth: 1,
              padding: { left: 6, right: 6, top: 2, bottom: 2 },
            },
          },
        },
      ],
    };
  }, [thresholdValue, threshold_label]);

  // ── Horizontal scroll ──────────────────────────────────────────────────────
  const chartWidth = Math.max(700, xCategories.length * 90);
  const chartWrapperRef = useRef<HTMLDivElement>(null);

  // ── ApexCharts options ─────────────────────────────────────────────────────
  const isSinglePlayer = selectedCount === 1;

  const options: ApexOptions = useMemo((): ApexOptions => ({
    chart: {
      type: "line",
      background: "transparent",
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      animations: { enabled: true, speed: 650 },
      height: 380,
      events: { mounted: (c: any) => fixMarkerOverlaps(c.el), updated: (c: any) => fixMarkerOverlaps(c.el) },
    },
    colors: selectedIndicesOrdered.map((playerIdx) => playerColorMap.get(playerIdx)!),
    stroke: { curve: "smooth", width: 2.5, lineCap: "round" },
    markers: {
      size: 6,
      strokeWidth: 2.5,
      strokeColors: "#ffffff",
      strokeOpacity: 1,
      hover: { size: 6 },
      onClick: (_e: any) => {
        const wrapper = chartWrapperRef.current;
        if (!wrapper) return;
        const point = _e.target;
        const pointX = point.getBoundingClientRect().left;
        const wrapperX = wrapper.getBoundingClientRect().left;
        wrapper.scrollTo({
          left: wrapper.scrollLeft + pointX - wrapperX - wrapper.clientWidth / 2,
          behavior: "smooth",
        });
      },
    },
    xaxis: {
      type: "category" as const,
      categories: xCategories,
      tickPlacement: "on" as const,
      labels: {
        style: {
          colors: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        rotate: isSinglePlayer && xCategories.length > 8 ? -35 : 0,
        rotateAlways: false,
        hideOverlappingLabels: true,
      },
      axisBorder: { show: true, color: "#e8edf3", height: 1 },
      axisTicks: { show: false },
      title: {
        text: isSinglePlayer ? "Game Date" : "Game #",
        style: {
          color: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        offsetY: 6,
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        formatter: (val: number) => val.toFixed(0),
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      title: {
        text: statLabel,
        style: {
          color: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        offsetX: -4,
      },
    },
    ...(thresholdAnnotation ? { annotations: thresholdAnnotation } : {}),
    grid: {
      show: true,
      borderColor: "#f1f5f9",
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 10, right: 20, bottom: 4, left: 8 },
    },
    legend: { show: false },
    tooltip: {
      theme: "light",
      shared: false,
      intersect: true,
      fixed: { enabled: false },
      followCursor: true,
      custom: ({ seriesIndex, dataPointIndex }) => {
        if (seriesIndex >= selectedIndicesOrdered.length) return "";
        const playerIdx = selectedIndicesOrdered[seriesIndex];
        const player = players[playerIdx];
        if (!player) return "";
        const games = gameLookup[seriesIndex];
        const game = games?.[dataPointIndex];
        if (!game) return "";
        const color = PALETTE[playerIdx % PALETTE.length];
        return buildTooltip(player, game, color, statLabel);
      },
    },
    dataLabels: { enabled: false },
  }), [
    selectedIndicesOrdered, xCategories, statLabel, gameLookup,
    thresholdAnnotation, isSinglePlayer, players,
  ]);

  // ── Empty state ────────────────────────────────────────────────────────────
  if (!players || players.length === 0) {
    return (
      <>
        <style>{css}</style>
        <div className="ctg">
          <div className="ctg-empty" style={{ height: 360 }}>
            <span className="ctg-empty-icon">📭</span>
            <span>No count data available</span>
          </div>
        </div>
      </>
    );
  }

  const allSelected = selectedPlayers.size === Math.min(2, players.length);

  return (
    <>
      <style>{css}</style>
      <div className="ctg">

        {selectedPlayers.size >= 2 && (
  <div style={{ position:"relative", zIndex:2, marginBottom:8, fontSize:11, color:"#b45309", fontWeight:600, background:"#fffbeb", border:"1px solid #fde68a", borderRadius:6, padding:"4px 12px", textAlign:"center" }}>
    Maximum 2 players can be compared at once. Deselect a player to add another.
  </div>
)}


        {/* Player chips */}
        <div className="ctg-chips-wrapper">
          <div className="ctg-chips">
            <button
              className={`ctg-chip-all${allSelected ? " active" : ""}`}
              onClick={selectAllPlayers}
            >
              {allSelected ? "✕ Clear" : `⊕ All (up to ${Math.min(2, players.length)})`}
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px" }}>
                ({players.length})
              </span>
            </button>

            {players.map((player, idx) => {
              const active = selectedPlayers.has(idx);
              const color = playerColorMap.get(idx) ?? "#cbd5e1";
              const label = labels[idx] ?? player.player_name;
              return (
                <button
                  key={idx}
                  className={`ctg-chip${active ? " active" : ""}`}
                  onClick={() => togglePlayer(idx)}
                  style={{
                    ["--chip-color" as any]: color,
                    ["--chip-bg" as any]: `${color}12`,
                    ["--chip-border" as any]: `${color}40`,
                    ["--chip-shadow" as any]: `${color}18`,
                  }}
                  title={`${player.player_name} · ${player.team_name}`}
                >
                  <span className="ctg-swatch" style={{ background: active ? color : "#cbd5e1" }} />
                  <span className="ctg-chip-name">{player.player_name}</span>
                  <span className="ctg-chip-stat-badge">
                    {player.total_games}g
                  </span>
                  {active && <span className="ctg-chip-check" style={{ color }}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Chart */}
        <div className="ctg-chart-wrapper" ref={chartWrapperRef}>
          {series.length > 0 && chartReady ? (
            <ReactApexChart
              type="line"
              series={series}
              options={options}
              height={380}
              width={chartWidth}
            />
          ) : (
            <div className="ctg-empty" style={{ height: 380 }}>
              <span className="ctg-empty-icon">↑</span>
              <span>Select players above to view counts</span>
            </div>
          )}
        </div>

      </div>
    </>
  );
};

// ── Tooltip builder ───────────────────────────────────────────────────────────

function buildTooltip(
  player: CountPlayer,
  game: CountGame,
  color: string,
  statLabel: string
): string {
  const displayDate = formatDateShort(game.game_date);

  return `
    <div style="
      background:#ffffff;
      border:1px solid #e2e8f0;
      border-top:3px solid ${color};
      border-radius:14px;
      padding:13px 15px;
      font-family:'Plus Jakarta Sans',sans-serif;
      box-shadow:0 8px 30px rgba(0,0,0,0.12),0 2px 8px rgba(0,0,0,0.06);
      min-width:210px;max-width:260px;
      position:relative;z-index:9999;
    ">
      <!-- Header -->
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
        <div style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;"></div>
        <span style="font-weight:700;color:#0f172a;font-size:13px;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
          ${player.player_name}
        </span>
        <span style="
          font-size:10px;font-weight:700;padding:2px 8px;border-radius:5px;
          color:${game.condition_met ? "#065f46" : "#991b1b"};
          background:${game.condition_met ? "#ecfdf5" : "#fef2f2"};
          border:1px solid ${game.condition_met ? "#6ee7b7" : "#fecaca"};
        ">
          ${game.condition_met ? "Met" : "Missed"}
        </span>
      </div>

      <!-- Date + Opponent -->
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:10px;">
        <div style="background:#f8fafc;border-radius:8px;padding:7px 9px;border:1px solid #f1f5f9;">
          <div style="font-family:'JetBrains Mono',monospace;font-size:8px;color:#94a3b8;text-transform:uppercase;margin-bottom:3px;">Date</div>
          <div style="font-size:11px;font-weight:600;color:#475569;">${displayDate}</div>
        </div>
        <div style="background:#f8fafc;border-radius:8px;padding:7px 9px;border:1px solid #f1f5f9;">
          <div style="font-family:'JetBrains Mono',monospace;font-size:8px;color:#94a3b8;text-transform:uppercase;margin-bottom:3px;">vs</div>
          <div style="font-size:11px;font-weight:600;color:#475569;">${game.opponent || "N/A"}</div>
        </div>
      </div>

      <!-- Stat + threshold -->
      <div style="
        display:flex;align-items:center;justify-content:space-between;
        background:${color}10;border:1px solid ${color}25;
        border-radius:10px;padding:9px 13px;
      ">
        <span style="font-family:'JetBrains Mono',monospace;font-size:9px;color:#64748b;text-transform:uppercase;">
          ${statLabel}
        </span>
        <span style="font-size:22px;font-weight:800;color:${color};letter-spacing:-0.02em;">
          ${game.stat_value}
        </span>
      </div>
    </div>`;
}

export default CountGraph;
